import { ActionBarPrimitive, AuiIf, MessagePrimitive, useAuiState } from "@assistant-ui/react";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { AGENT_NAME } from "./agentName";
import { ActivityIndicator } from "./ActivityIndicator";
import { copyToClipboard } from "./copyToClipboard";
import { messageCopyText } from "./genui/copyText";
import { GenUIComponentRegistry } from "./genui/GenUIComponentRegistry";
import { MarkdownText } from "./MarkdownText";
import { MessageFeedback } from "./MessageFeedback";
import { PendingIndicator } from "./PendingIndicator";
import { ToolFallback } from "./ToolFallback";
import { assistantMessageStyles as s } from "./AssistantMessage.styles";

type CopyStatus = "idle" | "copied" | "failed";

const COPY_FEEDBACK_MS = 2000;

function CopyButton() {
  // Text parts plus any GenUI card that has copy text (e.g. a conversation summary), so a
  // card-only reply is still copyable.
  const copyText = useAuiState((state) => messageCopyText(state.message.parts));
  const running = useAuiState(
    (state) => state.message.role === "assistant" && state.message.status?.type === "running",
  );
  const disabled = running || copyText.length === 0;
  const [status, setStatus] = useState<CopyStatus>("idle");

  const flash = (next: CopyStatus) => {
    setStatus(next);
    window.setTimeout(() => setStatus("idle"), COPY_FEEDBACK_MS);
  };

  const handleClick = async () => {
    if (!copyText) return;
    try {
      await copyToClipboard(copyText);
      flash("copied");
    } catch {
      flash("failed");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={status === "failed" ? "Couldn't copy message" : "Copy message"}
      className={status === "failed" ? `${s.copyBtn} ${s.copyBtnFailed}` : s.copyBtn}
    >
      {status === "copied" ? <Check size={13} className={s.copiedIcon} /> : <Copy size={13} />}
    </button>
  );
}

export function AssistantMessage() {
  return (
    <MessagePrimitive.Root aria-label={`${AGENT_NAME} response`} className={s.root}>
      <ActivityIndicator />
      <AuiIf
        condition={(st) => st.message.status?.type === "running" && st.message.parts.length === 0}
      >
        <PendingIndicator />
      </AuiIf>
      <AuiIf
        condition={(st) =>
          st.message.parts.length > 0 ||
          (st.message.status?.type === "incomplete" && st.message.status.reason === "error")
        }
      >
        <div className={s.bubble}>
          <div className={s.bubbleInner}>
            <MessagePrimitive.Parts
              components={{
                Text: MarkdownText,
                tools: {
                  Fallback: ToolFallback,
                  by_name: { genui: GenUIComponentRegistry },
                },
              }}
            />
            <MessagePrimitive.Error>
              <p className="mt-1 text-sm text-red-500">Something went wrong.</p>
            </MessagePrimitive.Error>
          </div>
          <ActionBarPrimitive.Root hideWhenRunning autohide="not-last" className={s.actionBar}>
            <MessageFeedback />
            <CopyButton />
          </ActionBarPrimitive.Root>
        </div>
      </AuiIf>
    </MessagePrimitive.Root>
  );
}
